import { AlertTriangle, Paperclip, Send } from "lucide-react";
import type React from "react";
import { useCallback, useRef, useState } from "react";
import { ChatState, useProject } from "@/features/project";
import { useTab } from "@/features/Tab";

export const ChatInputForm: React.FC = () => {
  const { chatState, submitChat, uploadAssetFiles, ownerEmail } = useProject();
  const { setViewMode } = useTab();

  const [input, setInput] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isDisabled = chatState !== ChatState.IDLE;

  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `min(${textarea.scrollHeight}px, calc(75 * var(--vh)))`;
  }, []);

  const handleFileUpload = useCallback(
    async (fileList: FileList): Promise<void> => {
      await uploadAssetFiles(fileList);
      setViewMode("assets");
    },
    [uploadAssetFiles, setViewMode],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmedInput = input.trim();
      if (!trimmedInput || isDisabled) return;

      setInput("");
      if (textareaRef.current) textareaRef.current.style.height = "";
      submitChat(trimmedInput);
    },
    [input, isDisabled, submitChat],
  );

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      await handleFileUpload(selectedFiles);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <form onSubmit={handleSubmit} className="relative">
      {ownerEmail && (
        <div className="flex items-center gap-1.5 px-2 py-1 mb-1 bg-orange-600 rounded-md text-white text-xs font-medium">
          <AlertTriangle size={12} />
          <span>Editing {ownerEmail}'s project</span>
        </div>
      )}
      <textarea
        ref={textareaRef}
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          resizeTextarea();
        }}
        onFocus={resizeTextarea}
        onBlur={() => {
          if (textareaRef.current) textareaRef.current.style.height = "";
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            handleSubmit(e);
          }
        }}
        placeholder="Ex: Create a 50% OFF promo for a coffee shop..."
        className={`w-full md:text-xs text-slate-200 placeholder-slate-500 p-2 pr-16 rounded-md focus:outline-none resize-none min-h-20 transition-[height] duration-150 ${
          ownerEmail
            ? "bg-orange-950 border-2 border-orange-500 focus:ring-1 focus:ring-orange-400"
            : "bg-slate-800 border border-slate-700 focus:ring-1 focus:ring-indigo-500"
        }`}
      />
      <div className="absolute bottom-3.5 right-2 flex items-center gap-1">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="p-1.5 text-slate-400 hover:text-slate-200 rounded-md hover:bg-slate-700"
          title="Upload Asset"
        >
          <Paperclip size={12} />
        </button>
        <button
          type="submit"
          disabled={!input.trim() || isDisabled}
          className={`p-1.5 rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
            ownerEmail
              ? "text-white bg-orange-600 enabled:hover:bg-orange-500"
              : "text-white enabled:hover:bg-indigo-500"
          }`}
        >
          <Send size={12} />
        </button>
      </div>
    </form>
  );
};
